/**
 * Site Access Utilities
 * 
 * Centralized functions for handling access control across the app.
 * Replaces hardcoded navigate("/regista-te") calls throughout the codebase.
 */

/**
 * Get the appropriate redirect path for unauthenticated users
 * based on the current site access mode.
 * 
 * @param {object} siteAccess - Site access settings from ConfigContext
 * @returns {string} - The path to redirect to
 */
export function getAuthRedirectPath(siteAccess) {
  const mode = siteAccess?.mode || 'open';
  
  // In authenticated_only mode, always go to login
  if (mode === 'authenticated_only') {
    return '/login';
  }
  
  // In other modes, go to regista-te (more inviting for new users)
  return '/regista-te';
}

/**
 * Check if a user can perform write operations
 * based on site access mode and user role.
 * 
 * @param {object} siteAccess - Site access settings from ConfigContext
 * @param {object} user - Current user object (null if anonymous)
 * @returns {boolean} - Whether write operations are allowed
 */
export function canWrite(siteAccess, user) {
  const mode = siteAccess?.mode || 'open';
  
  // Open mode - everyone can write (if authenticated)
  if (mode === 'open') {
    return !!user;
  }
  
  // Authenticated only - authenticated users can write
  if (mode === 'authenticated_only') {
    return !!user;
  }
  
  // Read only - only owner/admin can write (and only in admin area)
  if (mode === 'read_only') {
    return user?.role === 'owner' || user?.role === 'admin';
  }
  
  // Maintenance - only owner/admin can access admin
  if (mode === 'maintenance') {
    return user?.role === 'owner' || user?.role === 'admin';
  }
  
  return false;
}

/**
 * Check if a user can access a specific path
 * based on site access mode and user role.
 * 
 * @param {string} path - The path to check
 * @param {object} siteAccess - Site access settings from ConfigContext
 * @param {object} user - Current user object (null if anonymous)
 * @returns {object} - { allowed: boolean, redirect: string|null }
 */
export function checkPathAccess(path, siteAccess, user) {
  const mode = siteAccess?.mode || 'open';
  const isAuthenticated = !!user;
  const isOwnerOrAdmin = user?.role === 'owner' || user?.role === 'admin';
  
  // Paths always accessible
  const alwaysAccessible = [
    '/login', '/auth', '/termos', '/privacidade', 
    '/esqueci-password', '/repor-password', '/verificar-email',
    '/confirmar-email', '/aguardar-aprovacao'
  ];
  
  if (alwaysAccessible.some(p => path.startsWith(p))) {
    return { allowed: true, redirect: null };
  }
  
  // Admin area - owner/admin always have access
  if (path.startsWith('/admin')) {
    if (isOwnerOrAdmin) {
      return { allowed: true, redirect: null };
    }
    return { allowed: false, redirect: '/login' };
  }
  
  // Handle by mode
  switch (mode) {
    case 'maintenance':
      // Only owner/admin can access anything (except login)
      if (!isOwnerOrAdmin) {
        return { allowed: false, redirect: '/manutencao' };
      }
      return { allowed: true, redirect: null };
      
    case 'authenticated_only':
      // Anonymous users only see login
      if (!isAuthenticated) {
        return { allowed: false, redirect: '/login' };
      }
      return { allowed: true, redirect: null };
      
    case 'read_only':
    case 'open':
    default:
      // Normal access rules apply
      return { allowed: true, redirect: null };
  }
}

/**
 * List of paths that are public (accessible to anonymous users)
 * in "open" and "read_only" modes.
 */
export const PUBLIC_PATHS = [
  '/comunidade',
  '/passeios',
  '/passeio/', // Trip details (with limitations)
  '/clubes',
  '/clube/', // Club details (with limitations)
  // Legacy routes for backwards compatibility
  '/grupos',
  '/grupo/',
  '/regista-te',
  '/sobre',
  '/faq',
  '/ajuda',
  '/privacidade',
  '/contactos',
  '/registar',
];

/**
 * List of paths that are restricted (require authentication)
 */
export const RESTRICTED_PATHS = [
  '/meu-canto',
  '/garagem',
  '/meus-passeios',
  '/meus-clubes',
  // Legacy route
  '/meus-grupos',
  '/minhas-colaboracoes',
  '/minhas-participacoes',
  '/perfil',
  '/admin',
  '/passeio/novo',
  '/clube/novo',
  // Legacy route
  '/grupo/novo',
];

/**
 * Check if a path is public
 * @param {string} path - The path to check
 * @returns {boolean}
 */
export function isPublicPath(path) {
  return PUBLIC_PATHS.some(p => path.startsWith(p));
}

/**
 * Check if a path is restricted
 * @param {string} path - The path to check  
 * @returns {boolean}
 */
export function isRestrictedPath(path) {
  return RESTRICTED_PATHS.some(p => path.startsWith(p));
}

/**
 * Handle unauthorized access by redirecting to the appropriate page.
 * This function centralizes all redirects for unauthenticated users.
 * 
 * When a user's session expires, they should ALWAYS go to /login (not /regista-te),
 * because they were already a registered user.
 * 
 * @param {function} navigate - React Router's navigate function
 * @param {object} options - Optional configuration
 * @param {string} options.returnTo - Path to return to after authentication
 * @param {string} options.message - Optional message to display on the auth page
 * @param {object} siteAccess - Site access settings from ConfigContext (optional)
 */
export function handleUnauthorizedAccess(navigate, options = {}, siteAccess = null) {
  // Visitor clicking on a protected feature → /regista-te (registration)
  const redirectPath = '/regista-te';
  const state = {};
  
  if (options.returnTo) {
    const safe = sanitizeReturnTo(options.returnTo);
    if (safe) {
      state.returnTo = safe;
      // Persist so it survives OAuth round-trips and page hops
      try { sessionStorage.setItem('returnTo', safe); } catch {}
    }
  }
  if (options.message) {
    state.message = options.message;
  }
  
  navigate(redirectPath, Object.keys(state).length > 0 ? { state, replace: true } : { replace: true });
}

/**
 * Validate a post-login return path. Only internal absolute paths are
 * allowed (prevents open-redirect). Auth pages are rejected to avoid loops.
 */
export function sanitizeReturnTo(raw) {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/')) return null;                       // internal only
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null; // no protocol-relative
  const authPrefixes = ['/login', '/registar', '/regista-te', '/auth/', '/verificar-email', '/repor-password', '/esqueci-password'];
  if (authPrefixes.some((p) => raw === p || raw.startsWith(p))) return null;
  return raw;
}

/**
 * Resolve the destination after a successful login.
 * Returns a safe internal path or null (caller falls back to default).
 * Admin-only paths are ignored for non-admin users.
 */
export function resolvePostLoginPath(rawReturnTo, isAdminUser) {
  const safe = sanitizeReturnTo(rawReturnTo);
  if (!safe) return null;
  if (safe.startsWith('/admin') && !isAdminUser) return null;
  return safe;
}

/** Read + clear the persisted returnTo from sessionStorage. */
export function consumeStoredReturnTo() {
  let v = null;
  try {
    v = sessionStorage.getItem('returnTo');
    sessionStorage.removeItem('returnTo');
  } catch {}
  return v;
}
